import Cookies from "js-cookie";
import service from '@/utils/request.js'
import { Notification } from 'element-ui';

/**
 * WebSocket连接管理器类
 * 负责管理WebSocket连接和消息处理
 */
class WebSocketManager {
  // 静态属性：localStorage广播键名
  static STORAGE_KEY = 'remote_push_4bbefa148058f3e11316f4bc26bb758b';
  // 静态属性：自定义事件名
  static DIY_EVENT_NAME = 'remote_push';

  constructor() {
    // WebSocket连接实例
    this.websocketInstance = null;
    
    // 连接锁，防止并发连接
    this.isConnecting = false;
    this.connectingPromise = null;
    
    // 重连相关
    this.reconnectTimer = null;
    this.reconnectAttempts = 0;
    this.MAX_RECONNECT_ATTEMPTS = 5;
    this.RECONNECT_INTERVAL = 3000;

    // 初始化广播监听器
    this._initBroadcastListener();
  }

  /**
   * 获取公司ID
   */
  getCompanyId() {
    return Cookies.get("companyId");
  }

  /**
   * 获取WebSocket Token
   */
  async _getWebSocketToken() {
    const clientId = this.getCompanyId();
    return service.post(
      `/smart/health/api/ws/dcsw/generateTokenWithCompanyId?companyId=${clientId}`
    );
  }

  /**
   * 获取基础API地址
   */
  _gainBaseApi() {
    return process.env.BASE_API.replace(/https?:\/\//g, "");
  }

  /**
   * 获取WebSocket协议前缀
   */
  _gainPrefix() {
    const protocol = process.env.BASE_API.trim().toLowerCase().split("://")[0];
    if (protocol === "https") {
      return "wss";
    } else if (protocol === "http") {
      return "ws";
    } else {
      throw new Error("链接websocket时，主机域名找不到http或https协议");
    }
  }

  /**
   * 创建WebSocket连接
   */
  async createConnection() {
    // 如果正在连接中，返回现有的Promise，避免重复连接
    if (this.isConnecting && this.connectingPromise) {
      // console.log('WebSocket正在连接中，复用现有连接');
      return this.connectingPromise;
    }
    
    // 如果已有连接且状态正常，直接返回
    if (this.websocketInstance && this.websocketInstance.readyState === WebSocket.OPEN) {
      // console.log('WebSocket已连接，无需重复连接');
      return Promise.resolve(this.websocketInstance);
    }
    
    // 设置连接锁
    this.isConnecting = true;
    
    this.connectingPromise = this._doCreateConnection();
    
    try {
      const result = await this.connectingPromise;
      return result;
    } finally {
      this.isConnecting = false;
      this.connectingPromise = null;
    }
  }
  
  /**
   * 实际创建WebSocket连接的内部方法
   */
  async _doCreateConnection() {
    try {
      // 如果已有连接，先关闭
      if (this.websocketInstance && this.websocketInstance.readyState !== WebSocket.CLOSED) {
        // console.log("已有WebSocket连接，先关闭");
        this.closeConnection();
      }

      const result = await this._getWebSocketToken();
      const token = result.body.data;
      const base_api = this._gainBaseApi();
      
      // 创建WebSocket连接
      this.websocketInstance = new WebSocket(
        `${this._gainPrefix()}://${base_api}/ws/websocket/medicine_${this.getCompanyId()}?token=${token}`
      );

      this.websocketInstance.onopen = () => {
        // console.log("WebSocket连接成功");
        this.reconnectAttempts = 0; // 重置重连次数
        
        // 清除重连定时器
        if (this.reconnectTimer) {
          clearTimeout(this.reconnectTimer);
          this.reconnectTimer = null;
        }
      };

      this.websocketInstance.onmessage = (event) => {
        // console.log("收到WebSocket消息:", event.data);
        // 处理消息
        this._handleMessage(event.data);
      };

      this.websocketInstance.onerror = (event) => {
        console.error("WebSocket连接错误:", event);
      };

      this.websocketInstance.onclose = (event) => {
        // console.log("WebSocket连接关闭:", event.code, event.reason);
        this.websocketInstance = null;
        
        // 如果不是主动关闭，尝试重连
        if (event.code !== 1000 && this.reconnectAttempts < this.MAX_RECONNECT_ATTEMPTS) {
          this._attemptReconnect();
        }
      };

      return this.websocketInstance;
    } catch (error) {
      console.error("创建WebSocket连接失败:", error);
      throw error;
    }
  }

  /**
   * 处理WebSocket消息
   */
  _handleMessage(messageId) {
    // 后端返回的是唯一ID
    // console.log("WebSocket收到消息ID:", messageId);
    
    // 创建广播数据
    const broadcastData = {
      id: messageId,
      timestamp: Date.now()
    };
    
    // 通过localStorage广播到其他标签页
    try {
      localStorage.setItem(WebSocketManager.STORAGE_KEY, JSON.stringify(broadcastData));
    } catch (error) {
      console.error("广播消息到其他标签页失败:", error);
    }
    
    // 在当前标签页触发事件
    window.dispatchEvent(new CustomEvent(WebSocketManager.DIY_EVENT_NAME, {
      detail: { 
        id: messageId, 
        timestamp: broadcastData.timestamp,
        fromBroadcast: false // 标识这是本标签页直接收到的消息
      }
    }));
  }

  /**
   * 初始化广播监听器
   * 监听localStorage变化，实现跨标签页消息同步
   */
  _initBroadcastListener() {
     // 监听storage事件，实现跨标签页通信
     window.addEventListener('storage', (event) => {
       if (event.key === WebSocketManager.STORAGE_KEY && event.newValue) {
         try {
          const broadcastData = JSON.parse(event.newValue);
          // console.log("收到跨标签页广播消息:", broadcastData);
          
          // 在当前标签页触发websocket-notification事件
          window.dispatchEvent(new CustomEvent(WebSocketManager.DIY_EVENT_NAME, {
            detail: {
              id: broadcastData.id,
              timestamp: broadcastData.timestamp,
              fromBroadcast: true // 标识这是来自广播的消息
            }
          }));
        } catch (error) {
          console.error("解析跨标签页广播消息失败:", error);
        }
      }
    });
  }

  /**
   * 尝试重连
   */
  _attemptReconnect() {
    if (this.reconnectAttempts >= this.MAX_RECONNECT_ATTEMPTS) {
      // console.error("WebSocket重连次数已达上限，停止重连");
      // Notification({
      //     title: '警告',
      //     message: '智能药店新检测数据通知连接失败',
      //     duration: 0,
      //     type: "error"
      //   })
      return;
    }

    this.reconnectAttempts++;
    console.log(`WebSocket重连尝试 ${this.reconnectAttempts}/${this.MAX_RECONNECT_ATTEMPTS}`);

    this.reconnectTimer = setTimeout(() => {
      this.createConnection().catch(error => {
        console.error("WebSocket重连失败:", error);
      });
    }, this.RECONNECT_INTERVAL);
  }

  /**
   * 关闭WebSocket连接
   */
  closeConnection() {
    if (this.websocketInstance) {
      // console.log("主动关闭WebSocket连接");
      this.websocketInstance.close(1000, "用户主动关闭");
      this.websocketInstance = null;
    }

    // 清除重连定时器
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }

    // 重置重连次数
    this.reconnectAttempts = 0;

    // 清理localStorage中的广播消息
     try {
       localStorage.removeItem(WebSocketManager.STORAGE_KEY);
     } catch (error) {
      console.error("清理localStorage广播消息失败:", error);
    }
  }

  /**
   * 获取WebSocket连接状态
   */
  getStatus() {
    if (!this.websocketInstance) {
      return 'CLOSED';
    }
    
    switch (this.websocketInstance.readyState) {
      case WebSocket.CONNECTING:
        return 'CONNECTING';
      case WebSocket.OPEN:
        return 'OPEN';
      case WebSocket.CLOSING:
        return 'CLOSING';
      case WebSocket.CLOSED:
        return 'CLOSED';
      default:
        return 'UNKNOWN';
    }
  }

  /**
   * 发送消息
   */
  sendMessage(message) {
    if (this.websocketInstance && this.websocketInstance.readyState === WebSocket.OPEN) {
      this.websocketInstance.send(JSON.stringify(message));
      return true;
    } else {
      console.warn("WebSocket未连接，无法发送消息");
      return false;
    }
  }
}

// 创建单例实例
const webSocketManager = new WebSocketManager();

// 导出类和实例
export { WebSocketManager };
export default webSocketManager;



// WEBPACK FOOTER //
// ./src/utils/webSocketLink.js